import { CenterAvailableDTO, CenterDTO } from '@/models/CenterDTO';
import { EventDTO } from '@/models/EventDTO';
import { SelectOptionDTO } from '@/models/SelectOptionDTO';
import AppointmentService from '@/services/appointment.service';
import { ActionTypes, GetterTypes } from '@/store-vuex/types';
import moment from 'moment-timezone';
import { Component, Vue, Watch } from 'vue-property-decorator';
import InputDate from '../inputs/InputDate/InputDate';
import InputSelect from '../inputs/InputSelect/InputSelect';
import CenterService from '@/services/center.service';
import { AppointmentDTO } from '@/models/AppointmentDTO';
import InformationBlock from '../InformationBlock/InformationBlock';
import { McsisConstants } from '@/models/constants/McsisConstants';

@Component({
    components: {
        InputSelect,
        InputDate,
        InformationBlock,
    },
})
export default class AvailableAppointments extends Vue {
    public EventsAvailable: EventDTO[] = [];
    public referenceDate: string = '';
    public options: SelectOptionDTO[] = [];
    public dates: string[] = [];

    /**
     * Gets the data loading state of the application
     * @returns {boolean} True if the data is loading, otherwise false
     */
    public get loading(): boolean {
        return this.$store.getters[GetterTypes.dataLoading];
    }

    /**
     * Function called in the .vue file in order to format the selected center start date
     *
     * @param date The startDate field value
     * @returns The formatted date withtout time
     */
    public formatCenterDate(date: string): string {
        return moment(date).format(McsisConstants.DefaultDateFormat);
    }

    /**
     * Function called in the .vue file in order to format the slot start time
     *
     * @param date The eventStart field value
     * @returns The formatted time HH:mm
     */
    public formatSlotStartTime(date: string): string {
        return moment(date).format(McsisConstants.SlotTimeFormat);
    }

    /**
     * Function called when a text is entered in the input field of the datepicker component
     *
     * @param value The string date filled in
     */
    public onDateInput(value: string): void {
        const date = moment(value);
        this.referenceDate = date.isValid()
            ? date.format(McsisConstants.DefaultDateFormat)
            : McsisConstants.EmptyString;
    }

    /**
     * Function called when the selected date is incremented from the calendar
     *
     * @param value The increment to add in days
     */
    public onIncrementDateClick(value: number) {
        const newDate = moment(this.referenceDate)
            .add(value, McsisConstants.SubstractDaysParameter)
            .format(McsisConstants.DefaultDateFormat);

        this.referenceDate = newDate > this.centerSelected.startDate
            ? newDate
            : this.centerSelected.startDate;
    }

    /**
     * Function called when the selected center changes
     *
     * @param selectedValue The new selected center
     */
    public onSelectChange(selectedValue: string) {
        // change value of center selected in the store
        this.$store.dispatch(ActionTypes.setCenterSelected, this.centerAvailable.find(
            (center: CenterAvailableDTO) => center.personId === Number(selectedValue)));
        this.referenceDate = moment(this.centerSelected.startDate).format(McsisConstants.DefaultDateFormat);

        this.generateDates();
    }

    /**
     * Function called when an available slot time is selected
     *
     * @param event The EventDTO instance for the selected slot time
     */
    public async onEventClick(event: EventDTO) {
        const app = this.$store.getters[GetterTypes.appointment];
        const center = await CenterService.getCenterById(Number(this.centerSelected.personId));

        app.appDate = moment(event.start).format();
        app.appEndDate = moment(event.end).format();
        app.slotId = event.slotId;
        app.rooId = event.rooId;
        app.center = center as CenterDTO;

        this.$store.commit(ActionTypes.setAppointment, app);
        this.$emit('eventSelected');

        this.generateDates();
    }

    /**
     * Function called to know the CSS style to apply to the button for the slot times when clicked
     *
     * @param event The selected EventDTO instance
     *
     * @returns true if selected, otherwise false
     */
    public isEventSelected(event: EventDTO): boolean {
        // true if all
        return this.appointment.center != null &&
            event.centerId === this.appointment.center.personId &&
            this.appointment.appDate != null &&
            moment(event.start).format(McsisConstants.SwissDateFormat) ===
                    moment(this.appointment.appDate).format(McsisConstants.SwissDateFormat) &&
            moment(event.start).format(McsisConstants.SlotTimeFormat) ===
                    moment(this.appointment.appDate).format(McsisConstants.SlotTimeFormat);
    }

    /**
     * Gets the centers with availabilities
     *
     * @returns {CenterAvailableDTO[]} The centers with availabilities
     */
    public get centerAvailable() {
        return this.$store.getters[GetterTypes.centerWithAvailabilities];
    }

    /**
     * Gets the selected center
     *
     * @returns {CenterAvailableDTO} The selected center
     */
    public get centerSelected(): CenterAvailableDTO {
        return this.$store.getters[GetterTypes.centerSelected];
    }

    /**
     * Updates the available slots when the selected center changes
     */
    @Watch('referenceDate')
    protected onCenterSelectedChanged() {
        if (this.centerSelected.personId !== undefined) {
            this.generateDates();
        }
    }

    /**
     * Initializes the component data
     */
    protected async mounted() {
        this.$store.dispatch(ActionTypes.setDataLoading, true);

        await this.fetchCenterAvailable();
        this.generateOptions();

        if (this.appointment.center != null) {
          const ecId = this.appointment.center.personId;
          if (this.centerAvailable.find((center: CenterAvailableDTO) =>
                center.personId === Number(ecId)) !== undefined) {
                this.$store.dispatch(ActionTypes.setCenterSelected, this.centerAvailable.find(
                    (center: CenterAvailableDTO) => center.personId === Number(ecId)),
            );
          }
        }

        if (this.appointment.appDate != null && moment(this.appointment.appDate) > moment()) {
            this.referenceDate = moment(this.appointment.appDate).format(McsisConstants.DefaultDateFormat);
        } else if (this.centerSelected.personId !== undefined) {
            this.referenceDate = moment(this.centerSelected.startDate).format(McsisConstants.DefaultDateFormat);
        } else {
            this.referenceDate = moment().format(McsisConstants.DefaultDateFormat);
        }

        this.$store.dispatch(ActionTypes.setDataLoading, false);
    }

    /**
     * @brief Get center list
     */
    protected async fetchCenterAvailable() {
        this.$store.dispatch(ActionTypes.setCenterWithAvailabilities, []);
        if (this.appointment.partId) {
        await AppointmentService.GetFirstSlot(this.rdvLong, this.appointment.partId)
            .then((res: any) => {
                this.$store.dispatch(ActionTypes.setCenterWithAvailabilities, res as CenterAvailableDTO[]);
            });
        }
    }

    /**
     * @brief fetch free slots
     */
    protected async generateDates() {
        if (this.centerSelected != null && this.referenceDate < this.formatCenterDate(this.centerSelected.startDate)) {
            this.EventsAvailable = [];
        } else if (this.appointment.partId) {
            await AppointmentService.GetSlotsByCenterId(
                this.centerSelected.personId,
                this.appointment.partId,
                this.referenceDate,
                this.rdvLong)
            .then((res: EventDTO[]) => {
                this.EventsAvailable = res;
            })
            .catch( () => {
                this.EventsAvailable = [];
            });
        }
    }

    /**
     * Gets true if the appointment is a long one, otherwise false
     */
    private get rdvLong(): boolean  {
        return this.$store.getters[GetterTypes.isRdvLong];
    }

    /**
     * Gets the current AppointmentDTO instance
     *
     * @returns {AppointmentDTO} The current AppointmentDTO instance
     */
    private get appointment(): AppointmentDTO  {
        return this.$store.getters[GetterTypes.appointment];
    }

    /**
     * Generates the list of values for the center dropdown list with availabilities
     */
    private generateOptions() {
        let text = '';
        this.centerAvailable.forEach((center: CenterAvailableDTO) => {
            text = center.companyName + ' ('
                + this.$t('page.appointmentSelection.nextAvailability')
                + moment(center.startDate).format(McsisConstants.SwissDateFormat) + ')';

            this.options.push({value: center.personId.toString(), text} as SelectOptionDTO);
        });
    }
}
